import {
    getNewBasicOrderJsonSchema,
    getGenericDefinitionsAsJsonSchemaDefinitions,
    convertInlineDefsToRefs,
} from '../newBasicOrderJsonSchema';
import { log } from './log';
import { createLeg } from './types';
import type { Leg, TradeProState, TradeProMultiLegState, PriceTicker, OrderData, BaseSchema } from './types';
import { resolveSchema } from '@jsonforms/core';
import type { JsonSchema } from '@jsonforms/core';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { isEqual, toMerged } from 'es-toolkit';
import { ignoredProps, hiddenProps, orderedProps, fakeRequired } from 'src/components/JsonForm/ThinkalphaTheme';
import { createInitialState as createSymbolPickerInitialState } from 'src/features/symbol-picker/state';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { getUnderlyingSymbolFromInstrument } from 'src/lib/instrumentUtils';
import { filterSchemaExDestinations } from 'src/lib/trading/destinationFilter/filterDestinations';
import { DEFAULT_ROUTE, DEFAULT_EXDESTINATION } from 'src/models/TradeProWidgetModel';
import { JsonFormGeneratorServiceImpl } from 'src/routes/widgets/TradeProWidget/state/JsonFormGeneratorService';
import { z } from 'zod';

/**
 * Core properties that should always be preserved in orderData regardless of schema.
 * These are the minimal fields required for a valid order.
 */
export const ORDER_CORE_PROPS = ['instrument', 'account', 'side', 'exDestination', 'route', 'type'] as const;

const routeSchemaValidator = z.object({
    'x-symbology': z.enum(['equity', 'option', 'generic']),
});

type RouteSymbology = z.infer<typeof routeSchemaValidator>['x-symbology'];

const parseRouteSymbology = (routeSchema: unknown, exDest: string, route: string): RouteSymbology | null => {
    const result = routeSchemaValidator.safeParse(routeSchema);
    if (!result.success) {
        log.warn('Route schema missing or invalid x-symbology', { exDest, route, issues: result.error.issues });
        return null;
    }
    return result.data['x-symbology'];
};

export const isMultiLegState = (state: TradeProState): state is TradeProMultiLegState => {
    return state.type === 'multi-leg';
};

export const getBasicExecutorsSchema = (): BaseSchema => {
    const schema: BaseSchema = {
        type: 'object',
        properties: {
            exDestination: {
                type: 'string',
                enum: ['basic'],
                default: 'basic',
            },
            route: {
                type: 'string',
                enum: ['basic'],
                default: 'basic',
            },
        },
        $defs: {
            ...getGenericDefinitionsAsJsonSchemaDefinitions(),
            basic: {
                basic: convertInlineDefsToRefs(getNewBasicOrderJsonSchema()),
            },
        },
    };

    return schema;
};

export const calculateOrderPrices = (ticker: PriceTicker, value: number, type: string) => {
    let roundedNumber: number;
    if (ticker === 'bid') {
        roundedNumber = Math.floor(value * 100) / 100;
    } else if (ticker === 'ask') {
        roundedNumber = Math.ceil(value * 100) / 100;
    } else {
        roundedNumber = Math.round(value * 100) / 100;
    }

    const fixedNumber = roundedNumber.toFixed(2);
    const number = Number(fixedNumber);

    const newOrderData: Partial<OrderData> = {};

    switch (type) {
        case 'stop':
            newOrderData.stopPrice = number;
            break;
        case 'stop_limit':
            newOrderData.price = number;
            newOrderData.stopPrice = number;
            break;
        default:
            newOrderData.price = number;
            break;
    }
    return newOrderData;
};

export const setBasicSchemaForRoutelessExecutors = (
    baseSchema: BaseSchema,
    exDestinationsWithRoutes: Record<string, string[]>,
): BaseSchema => {
    const newSchema = structuredClone(baseSchema);

    for (const [exDestination, routes] of Object.entries(exDestinationsWithRoutes)) {
        if (routes.length === 0 || (routes.length === 1 && routes.includes(DEFAULT_ROUTE))) {
            newSchema.$defs[exDestination] = {};
            newSchema.$defs[exDestination][DEFAULT_ROUTE] = convertInlineDefsToRefs(getNewBasicOrderJsonSchema());
        }
    }

    return newSchema;
};

export const getExDestinationAndRouteFromSchemaDefaults = (
    schema: BaseSchema,
): { exDestination: string | undefined; route: string } => {
    const exDestination = schema?.properties?.exDestination?.default;
    const selectedSchema = schema?.$defs?.[exDestination];
    const availableRoutes = selectedSchema ? Object.keys(selectedSchema) : [];
    const route = availableRoutes[0] ?? DEFAULT_ROUTE;
    return { exDestination, route };
};

export const getAllowedInstrumentTypes = (baseSchema: BaseSchema): InstrumentType[] => {
    const instrumentTypesSet = new Set<InstrumentType>();

    (baseSchema.properties?.exDestination?.enum ?? []).forEach((exDest: string) => {
        if (exDest === 'basic') {
            return; // Skip 'basic' schema as it is generic and supposed to support all types
        }

        Object.keys(baseSchema.$defs?.[exDest] ?? {}).forEach((route) => {
            const routeSchema = baseSchema.$defs[exDest]?.[route];
            const symbology = parseRouteSymbology(routeSchema, exDest, route);

            if (symbology === null) {
                // Invalid or missing x-symbology, skip this route
                return;
            }

            switch (symbology) {
                case 'equity':
                    instrumentTypesSet.add('equity');
                    break;
                case 'option':
                    instrumentTypesSet.add('options');
                    break;
                case 'generic':
                    instrumentTypesSet.add('equity');
                    instrumentTypesSet.add('options');
                    break;
            }
        });
    });

    return instrumentTypesSet.size > 0 ? Array.from(instrumentTypesSet) : ['equity'];
};

const sideSupportsMultiLeg = (sideProperty: any, baseSchema: BaseSchema): boolean => {
    if (!sideProperty) return false;

    // Resolve $ref if present, otherwise use the property as-is
    const resolvedProperty = sideProperty.$ref ? resolveSchema(sideProperty, '', baseSchema) : sideProperty;

    // Handle case where resolveSchema returns undefined/null (invalid $ref)
    if (!resolvedProperty) return false;

    // Check for inline enum
    if (Array.isArray(resolvedProperty.enum)) {
        return resolvedProperty.enum.includes('multi-leg');
    }

    // Check for oneOf with multi-leg
    if (Array.isArray(resolvedProperty.oneOf)) {
        return resolvedProperty.oneOf.some((option: any) => option.const === 'multi-leg');
    }

    return false;
};

export const getExDestinationsWithRoutes = (
    baseSchema: BaseSchema,
    instrumentType: 'equity' | 'option',
    isMultiLeg?: boolean,
): Record<string, string[]> => {
    const exDestinationsWithRoutes: { [key: string]: string[] } = {};

    (baseSchema.properties?.exDestination?.enum ?? []).forEach((exDest: string) => {
        const allRoutes = Object.keys(baseSchema.$defs[exDest] ?? {});

        // If there are no routes at all, this is a routeless exDestination
        if (allRoutes.length === 0) {
            log.warn(`ExDestination '${exDest}' has no routes defined in schema`);

            // Routeless exDestinations can only be used for single-leg orders
            // They cannot support multi-leg since there's no explicit route with multi-leg support
            if (!isMultiLeg) {
                exDestinationsWithRoutes[exDest] = [DEFAULT_ROUTE];
            }
            return;
        }

        // Get all routes for the exDestination but filter those that do not match the instrument type
        const routes = allRoutes.filter((route) => {
            const routeSchema = baseSchema.$defs[exDest]?.[route];
            const symbology = parseRouteSymbology(routeSchema, exDest, route);

            if (symbology === null) {
                // Invalid or missing x-symbology, skip this route
                return false;
            }

            // For multi-leg orders, only check if the route supports multi-leg
            // (multi-leg orders can have mixed instrument types)
            if (isMultiLeg) {
                return sideSupportsMultiLeg(routeSchema?.properties?.side, baseSchema);
            }

            // For single-leg orders, check instrument type compatibility using x-symbology
            const instrumentTypeMatches =
                symbology === 'generic' ||
                (symbology === 'equity' && instrumentType === 'equity') ||
                (symbology === 'option' && instrumentType === 'option');

            return instrumentTypeMatches;
        });

        // Only include exDestination if it has at least one matching route
        if (routes.length > 0) {
            exDestinationsWithRoutes[exDest] = routes;
        }
    });

    return exDestinationsWithRoutes;
};

export const getSelectedExecutorSchema = (baseSchema: BaseSchema, exDestination: string) => {
    return baseSchema?.$defs?.[exDestination] ?? {};
};

export const getSelectedRoute = (availableRoutes: string[], selectedRoute: string) => {
    return availableRoutes.includes(selectedRoute) ? selectedRoute : availableRoutes[0] || DEFAULT_ROUTE;
};

export const getDefaultOrderDataFromSelectedExecutorSchema = (
    schemaForExchange: BaseSchema,
    selectedRoute: string,
): Partial<OrderData> => {
    const extractDefaults = (properties: Record<string, any>): any => {
        const defaults: any = {};
        for (const key of Object.keys(properties)) {
            const property = properties[key];
            if (property.type === 'object' && property.properties) {
                defaults[key] = extractDefaults(property.properties);
            } else if ('default' in property) {
                defaults[key] = property.default;
            }
        }
        return defaults;
    };

    const schema = schemaForExchange[selectedRoute] as JsonSchema | undefined;
    if (schema && schema.properties) {
        return extractDefaults(schema.properties);
    }
    return {};
};

/**
 * Cleans up orderData by removing properties that don't exist in the schema.
 * This prevents validation errors when switching between schemas with different properties.
 *
 * @param orderData - The order data to clean
 * @param schema - The JSON schema to validate against
 * @returns Cleaned order data with only schema-valid properties
 */
export const filterOrderDataBySchema = (orderData: OrderData, schema: JsonSchema): OrderData => {
    if (!schema.properties) {
        return orderData;
    }

    // Properties from the schema
    const schemaProps = schema?.properties ? Object.keys(schema.properties) : [];
    const propsToPersist = [...new Set([...ORDER_CORE_PROPS, ...schemaProps])];
    const filteredData: Partial<OrderData> = {};

    // Identify which properties are object types that need nested filtering
    const objectPropsToFilter = new Set<string>();
    for (const [propKey, propSchema] of Object.entries(schema.properties)) {
        if (
            propSchema &&
            typeof propSchema === 'object' &&
            'type' in propSchema &&
            propSchema.type === 'object' &&
            'properties' in propSchema &&
            propSchema.properties &&
            typeof propSchema.properties === 'object'
        ) {
            objectPropsToFilter.add(propKey);
        }
    }

    // Copy properties to filteredData, excluding object-type properties that will be filtered separately
    propsToPersist.forEach((prop: string) => {
        if (prop in orderData && !objectPropsToFilter.has(prop)) {
            filteredData[prop] = orderData[prop];
        }
    });

    // Filter nested object properties based on the schema
    for (const [propKey, propSchema] of Object.entries(schema.properties)) {
        if (objectPropsToFilter.has(propKey)) {
            const oldNestedData = orderData[propKey];
            if (oldNestedData && typeof oldNestedData === 'object') {
                const filteredNestedData: Record<string, any> = {};
                const validNestedKeys = Object.keys(propSchema.properties);
                // Only keep nested properties that are defined in the schema
                for (const nestedKey of validNestedKeys) {
                    if (nestedKey in oldNestedData) {
                        filteredNestedData[nestedKey] = oldNestedData[nestedKey];
                    }
                }
                filteredData[propKey] = filteredNestedData;
            } else if (propKey in orderData) {
                // Initialize as empty object if the property existed but was not an object
                filteredData[propKey] = {};
            }
        }
    }

    return filteredData as OrderData;
};

export const setSchemaAndUISchemaAfterOrderDataChange = (newState: TradeProState) => {
    const formService = new JsonFormGeneratorServiceImpl();
    const newSchema = formService.generateSchema(newState);
    newState.schema = isEqual(newSchema, newState.schema) ? newState.schema : newSchema;

    const newUISchema = formService.generateUISchema({
        schema: newState.schema,
        ignoredProps,
        hiddenProps,
        orderedProps,
        fakeRequired,
        orderData: newState.orderData,
        selectedAccountEffectiveAccess: newState.selectedAccountEffectiveAccess,
    });
    newState.uischema = isEqual(newUISchema, newState.uischema) ? newState.uischema : newUISchema;

    return newState;
};

/**
 * Generates and returns only the schema for the given state.
 * Does NOT modify the state or clean up orderData.
 * Use this for intermediate calculations where you need the schema but don't want side effects.
 */
export const generateSchemaForState = (state: TradeProState): JsonSchema => {
    const formService = new JsonFormGeneratorServiceImpl();
    return formService.generateSchema(state);
};

export const setSchemaChangesAfterInstrumentChange = (
    baseSchema: BaseSchema,
    instrument: Instrument,
    currentExDestination: string,
    currentRoute: string,
    isMultiLeg?: boolean,
) => {
    const exDestinationsWithRoutes = getExDestinationsWithRoutes(baseSchema, instrument.type, isMultiLeg);
    const allowedInstrumentTypes = getAllowedInstrumentTypes(baseSchema);

    const { exDestination: exDestinationDefault } = getExDestinationAndRouteFromSchemaDefaults(baseSchema);

    const exDestination =
        currentExDestination in exDestinationsWithRoutes
            ? currentExDestination
            : exDestinationDefault && exDestinationDefault in exDestinationsWithRoutes
              ? exDestinationDefault
              : DEFAULT_EXDESTINATION;

    const selectedExecutorSchema = getSelectedExecutorSchema(baseSchema, exDestination);
    // Use filtered routes for the instrument type
    const availableRoutes = exDestinationsWithRoutes[exDestination] || [DEFAULT_ROUTE];
    const route = getSelectedRoute(availableRoutes, currentRoute);

    return { exDestinationsWithRoutes, allowedInstrumentTypes, selectedExecutorSchema, exDestination, route };
};

export const newSymbolPickerStateFromExistingSymbol = (instrument: Instrument | undefined, legType: InstrumentType) => {
    const symbol = getUnderlyingSymbolFromInstrument(instrument);

    // For option legs, pass just the underlying symbol
    // For equity legs, pass the full symbol
    const instrumentString = legType === 'equity' ? symbol : symbol;

    return createSymbolPickerInitialState(instrumentString, [legType]);
};

export const recalculateLegQuantity = (leg: Leg, orderQuantity?: number): Leg => {
    return createLeg({
        ...leg,
        quantity: leg.quantityRatio * (orderQuantity || 1),
    });
};

export const handleFetchedExecutorSchema = (
    state: TradeProState,
    schema: BaseSchema,
    accountId?: string,
    skipDefaults?: boolean,
) => {
    // First, filter out invalid exDestinations and routes based on instrument type and multi-leg support
    // For some accounts filter the exDestinations based on the account ID. See PLAT-11238 for context
    const accountFilteredSchema = filterSchemaExDestinations(schema, accountId);

    const exDestinationsWithRoutes = getExDestinationsWithRoutes(
        accountFilteredSchema,
        state.orderData.instrument?.type || 'equity',
        isMultiLegState(state),
    );

    // Then, add basic schema only to the remaining valid routeless exDestinations
    const fixedSchema = setBasicSchemaForRoutelessExecutors(accountFilteredSchema, exDestinationsWithRoutes);

    const allowedInstrumentTypes = getAllowedInstrumentTypes(fixedSchema);

    // Check if current instrument type is still allowed after schema change
    // If not, reset to a default equity instrument to prevent invalid form state
    const currentInstrumentType = state.orderData.instrument?.type;
    const isCurrentInstrumentTypeAllowed =
        !currentInstrumentType ||
        allowedInstrumentTypes.length === 0 ||
        allowedInstrumentTypes.includes(currentInstrumentType === 'option' ? 'options' : 'equity');

    // Determine the instrument to use - reset if current type is no longer allowed
    const instrumentToUse = isCurrentInstrumentTypeAllowed
        ? state.orderData.instrument
        : state.orderData.instrument?.type === 'option' && state.orderData.instrument.underlyingInstrument
          ? // If we had an option, fall back to the underlying equity
            state.orderData.instrument.underlyingInstrument
          : // Otherwise use a default equity instrument
            {
                type: 'equity' as const,
                symbol: state.orderData.instrument?.type === 'equity' ? state.orderData.instrument.symbol : '',
            };

    const { exDestination: exDestinationDefault, route: routeDefault } =
        getExDestinationAndRouteFromSchemaDefaults(fixedSchema);

    const selectedExDestination =
        exDestinationDefault && exDestinationDefault in exDestinationsWithRoutes
            ? exDestinationDefault
            : state.orderData.exDestination && state.orderData.exDestination in exDestinationsWithRoutes
              ? state.orderData.exDestination
              : DEFAULT_EXDESTINATION;

    const selectedExecutorSchema = getSelectedExecutorSchema(fixedSchema, selectedExDestination);

    // Use filtered routes from exDestinationsWithRoutes for route selection
    const availableRoutes = exDestinationsWithRoutes[selectedExDestination] || [DEFAULT_ROUTE];
    const selectedRoute = getSelectedRoute(availableRoutes, routeDefault ?? state.orderData.route ?? DEFAULT_ROUTE);

    // Only apply default values on initial fetch, not on background refetches
    // This prevents overwriting user-entered form data
    let mergedOrderData;
    if (skipDefaults) {
        mergedOrderData = {
            ...state.orderData,
            instrument: instrumentToUse,
            exDestination: selectedExDestination,
            route: selectedRoute,
        };
    } else {
        const defaultOrderDataFromSelectedExecutorSchema = getDefaultOrderDataFromSelectedExecutorSchema(
            selectedExecutorSchema,
            selectedRoute,
        );

        mergedOrderData = {
            ...toMerged(state.orderData, defaultOrderDataFromSelectedExecutorSchema),
            instrument: instrumentToUse,
            exDestination: selectedExDestination,
            route: selectedRoute,
        };
    }

    // Generate the form schema to use for filtering
    const formSchema = generateSchemaForState({
        ...state,
        orderData: mergedOrderData,
        executorsSchema: fixedSchema,
        selectedExecutorSchema,
    } as TradeProState);

    // Filter the merged order data based on the new schema to remove extra properties
    const filteredOrderData = filterOrderDataBySchema(mergedOrderData, formSchema);

    // Handle multi-leg state to preserve legs
    if (isMultiLegState(state)) {
        return setSchemaAndUISchemaAfterOrderDataChange({
            ...state,
            orderData: {
                ...filteredOrderData,
                legs: state.orderData.legs,
            },
            executorsSchemaIsLoading: false,
            executorsSchemaFetchingError: null,
            executorsSchema: fixedSchema,
            exDestinationsWithRoutes,
            allowedInstrumentTypes,
            selectedExecutorSchema,
            fallbackToBasicSchema: false,
        });
    }

    return setSchemaAndUISchemaAfterOrderDataChange({
        ...state,
        orderData: filteredOrderData,
        executorsSchemaIsLoading: false,
        executorsSchemaFetchingError: null,
        executorsSchema: fixedSchema,
        exDestinationsWithRoutes,
        allowedInstrumentTypes,
        selectedExecutorSchema,
        fallbackToBasicSchema: false,
    } as TradeProState);
};

/**
 * Safely clone a JSON schema object, removing any non-serializable properties (like functions).
 * This is needed because schemas generated from Zod may contain non-cloneable properties.
 */
export const safeCloneSchema = (schema: any): any => {
    try {
        return JSON.parse(JSON.stringify(schema));
    } catch (error) {
        log.error('Failed to clone schema', { error, schema });
        return schema;
    }
};
